import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Text, ActionButton, Loading, IconButton, IconSynchronize } from '@carla/flora'
import { useAuth } from '@/app/hooks/useAuth'
import { useTemplates, type TemplateListItem } from '@/app/hooks/useTemplates'
import InspectionTypeBadge from '@/app/components/InspectionTypeBadge'

const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  borderBottom: '2px solid #e5e5e5',
}

const tableCellStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid #eee',
  verticalAlign: 'middle',
}

const INSPECTION_TYPE_ORDER = ['PICKUP', 'PRODUCTION', 'DELIVERY_PREP', 'MAINTENANCE'] as const

function inspectionTypeSortKey(value: string): number {
  const i = INSPECTION_TYPE_ORDER.indexOf(value as (typeof INSPECTION_TYPE_ORDER)[number])
  return i >= 0 ? i : INSPECTION_TYPE_ORDER.length
}

function sortTemplates(rows: TemplateListItem[]): TemplateListItem[] {
  return [...rows].sort((a, b) => {
    const flowCompare = (a.flow_code ?? '').localeCompare(b.flow_code ?? '', undefined, { sensitivity: 'base' })
    if (flowCompare !== 0) return flowCompare
    const typeCompare = inspectionTypeSortKey(a.inspection_type) - inspectionTypeSortKey(b.inspection_type)
    if (typeCompare !== 0) return typeCompare
    return (a.flow_name ?? '').localeCompare(b.flow_name ?? '', undefined, { sensitivity: 'base' })
  })
}


function FlowCodeCell({ value }: { value: string }) {
  const display = value ? value.toUpperCase() : value
  return <Text>{display}</Text>
}

export default function TemplatesTable() {
  const { getFirebaseToken } = useAuth()
  const navigate = useNavigate()
  const { data, isLoading, error, refresh } = useTemplates(getFirebaseToken)

  if (error) {
    return (
      <Box padding={4} space={4}>
        <Text color="background.destructive">Error loading templates: {error.message}</Text>
        <ActionButton onClick={refresh}>Retry</ActionButton>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box padding={4} alignItems="center" justifyContent="center">
        <Loading />
      </Box>
    )
  }

  const templates: TemplateListItem[] = data?.templates ?? []

  if (templates.length === 0) {
    return (
      <Box padding={5}>
        <Text variant="fir">Templates</Text>
        <Text>No templates found.</Text>
      </Box>
    )
  }

  return (
    <Box padding={5}>
      <Box direction="row" justifyContent="space-between" alignItems="center" marginBottom={4}>
        <Text variant="fir">Templates</Text>
        <IconButton
          icon={IconSynchronize}
          label="Refresh"
          onClick={refresh}
          disabled={isLoading}
          size={32}
          iconSize={18}
          color="foreground.secondary"
        />
      </Box>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '40%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={tableHeaderStyle}>
              <Text subStyle="emphasis">Flow name</Text>
            </th>
            <th style={tableHeaderStyle}>
              <Text subStyle="emphasis">Flow code</Text>
            </th>
            <th style={tableHeaderStyle}>
              <Text subStyle="emphasis">Inspection type</Text>
            </th>
            <th style={tableHeaderStyle}>
              <Text subStyle="emphasis">Template ID</Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortTemplates(templates).map((row) => (
            <tr
              key={row.template_id}
              onClick={() => navigate(`/templates/${encodeURIComponent(row.template_id)}`)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <td style={tableCellStyle}>
                <Text>{row.flow_name ?? '—'}</Text>
              </td>
              <td style={tableCellStyle}>
                <FlowCodeCell value={row.flow_code} />
              </td>
              <td style={tableCellStyle}>
                <InspectionTypeBadge value={row.inspection_type} />
              </td>
              <td style={tableCellStyle}>
                <Text>{row.template_id ?? '—'}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  )
}
